import type { ButtonHTMLAttributes, ReactNode } from 'react'
import { Link } from 'react-router-dom'
import styles from './primitives.module.css'

type Variant = 'primary' | 'tonal' | 'outline' | 'ghost' | 'cream'
type Size = 'sm' | 'md' | 'lg'

const variantClass: Record<Variant, string> = {
  primary: styles.primary,
  tonal: styles.tonal,
  outline: styles.outline,
  ghost: styles.ghost,
  cream: styles.cream,
}

function classes(variant: Variant, size: Size, className?: string) {
  return [styles.btn, variantClass[variant], size === 'lg' && styles.lg, size === 'sm' && styles.sm, className]
    .filter(Boolean)
    .join(' ')
}

type CommonProps = {
  children: ReactNode
  variant?: Variant
  size?: Size
  className?: string
}

// External hrefs open in a new tab by default. newTab={false} keeps marketing-to-app CTAs in the same tab.
export default function Button({
  children,
  variant = 'primary',
  size = 'md',
  className,
  to,
  href,
  newTab,
  ...rest
}: CommonProps &
  Partial<ButtonHTMLAttributes<HTMLButtonElement>> & {
    to?: string
    href?: string
    newTab?: boolean
  }) {
  const cls = classes(variant, size, className)
  if (to) {
    return (
      <Link to={to} className={cls}>
        {children}
      </Link>
    )
  }
  if (href) {
    const external = /^https?:\/\//.test(href)
    const openInNewTab = newTab ?? external
    return (
      <a
        href={href}
        className={cls}
        {...(openInNewTab ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      >
        {children}
      </a>
    )
  }
  return (
    <button type="button" className={cls} {...rest}>
      {children}
    </button>
  )
}
